import React, { useState, useRef, useEffect } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  Platform,
  Dimensions,
  Modal,
  Animated,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo from '../../components/HarmoniaLogo';

// ─── À créer à l'étape suivante ───────────────────────────────────────────────
import InfoBanner from '../../components/InfoBanner';
// ─────────────────────────────────────────────────────────────────────────────

// =====================================================
// IMPORTS DES COMPOSANTS DE JEUX
// =====================================================
// NOTE : ce fichier est dans app/(tabs)/, mais le dossier games/ est resté
// à la racine de app/ (app/games/...). On remonte donc d'un niveau ('../')
// au lieu de rester au même niveau ('./') comme avant le déplacement.
import VraiFaux from '../games/vrai-faux';
import Awale from '../games/awale';
import Dames from '../games/dames';
import Photo from '../games/photo';
import Comedie from '../games/comedie';
import Music from '../games/music';
import Djing from '../games/djing';

// ── Hook auto-hide (tab bar globale synchronisée au scroll) ─────────────────
import { useScrollAutoHide } from '../../hooks/useScrollAutoHide';

const { width, height } = Dimensions.get('window');

// =====================================================
// PALETTE HARMONIA
// =====================================================
const BRAND = {
  purple:      '#8A2BE2',
  purpleDark:  '#4B0082',
  purpleMid:   '#6A1BB2',
  orange:      '#FF8C00',
  orangeLight: '#FFA500',
  gold:        '#FFD700',
  bg:          '#0E0017',
  surface:     '#160020',
  card:        '#1C0030',
  border:      '#2E1050',
  text:        '#F5F0FF',
  textMuted:   '#9B82C4',
};

// =====================================================
// MÉTADONNÉES DES CATÉGORIES (icônes, couleurs, structure — non traduisibles)
// =====================================================
interface ItemMeta {
  id: string;
  accentIcon: string;
  color: string;
  gradientColors: [string, string];
  component: any;
}

interface CategoryMeta {
  id: string;
  ionIcon: string;
  gradient: [string, string];
  accentColor: string;
  items: ItemMeta[];
}

const CATEGORIES_META: CategoryMeta[] = [
  {
    id: 'strategie',
    ionIcon: 'extension-puzzle-outline',
    gradient: ['#1A0840', '#2D1469'],
    accentColor: '#A78BFA',
    items: [
      { id: 's1', accentIcon: 'ellipse', color: '#7C3AED', gradientColors: ['#4C1D95', '#6D28D9'], component: Awale },
      { id: 's2', accentIcon: 'grid',    color: '#8B5CF6', gradientColors: ['#3B0764', '#6D28D9'], component: Dames },
    ],
  },
  {
    id: 'connaissance',
    ionIcon: 'bulb-outline',
    gradient: ['#001830', '#002D50'],
    accentColor: '#38BDF8',
    items: [
      { id: 'c1', accentIcon: 'checkmark-circle', color: '#0EA5E9', gradientColors: ['#0C4A6E', '#0369A1'], component: VraiFaux },
    ],
  },
  {
    id: 'arts',
    ionIcon: 'color-palette-outline',
    gradient: ['#2D0A3A', '#4A1259'],
    accentColor: '#F472B6',
    items: [
      { id: 'a1', accentIcon: 'camera', color: '#EC4899', gradientColors: ['#831843', '#BE185D'], component: Photo },
      { id: 'a2', accentIcon: 'pencil', color: '#F97316', gradientColors: ['#7C2D12', '#C2410C'], component: Photo },
      { id: 'a3', accentIcon: 'shapes', color: '#A855F7', gradientColors: ['#4C1D95', '#7E22CE'], component: Photo },
    ],
  },
  {
    id: 'performance',
    ionIcon: 'mic-outline',
    gradient: ['#1A0800', '#351200'],
    accentColor: '#FBBF24',
    items: [
      { id: 'p1', accentIcon: 'happy', color: '#F59E0B', gradientColors: ['#78350F', '#B45309'], component: Comedie },
      { id: 'p2', accentIcon: 'body',  color: '#F43F5E', gradientColors: ['#881337', '#BE123C'], component: Comedie },
      { id: 'p3', accentIcon: 'film',  color: '#818CF8', gradientColors: ['#1E1B4B', '#3730A3'], component: Comedie },
    ],
  },
  {
    id: 'musique',
    ionIcon: 'musical-notes-outline',
    gradient: ['#001A1A', '#003030'],
    accentColor: '#2DD4BF',
    items: [
      { id: 'm1', accentIcon: 'mic',          color: '#14B8A6', gradientColors: ['#134E4A', '#0F766E'], component: Music },
      { id: 'm2', accentIcon: 'musical-note', color: '#06B6D4', gradientColors: ['#164E63', '#0E7490'], component: Music },
      { id: 'm3', accentIcon: 'radio',        color: '#3B82F6', gradientColors: ['#1E3A8A', '#1D4ED8'], component: Music },
    ],
  },
  {
    id: 'artisanat',
    ionIcon: 'construct-outline',
    gradient: ['#1A0C00', '#2E1800'],
    accentColor: '#F59E0B',
    items: [
      { id: 'ar1', accentIcon: 'cut',      color: '#D97706', gradientColors: ['#78350F', '#92400E'], component: Djing },
      { id: 'ar2', accentIcon: 'hammer',   color: '#A16207', gradientColors: ['#422006', '#713F12'], component: Djing },
      { id: 'ar3', accentIcon: 'sparkles', color: '#DB2777', gradientColors: ['#831843', '#9D174D'], component: Djing },
      { id: 'ar4', accentIcon: 'flame',    color: '#DC2626', gradientColors: ['#7F1D1D', '#991B1B'], component: Djing },
      { id: 'ar5', accentIcon: 'settings', color: '#64748B', gradientColors: ['#1E293B', '#334155'], component: Djing },
    ],
  },
];

function getCategories(t: (key: string) => string) {
  return CATEGORIES_META.map(cat => ({
    id: cat.id,
    title: t(`gamesScreen.categories.${cat.id}.title`),
    ionIcon: cat.ionIcon,
    gradient: cat.gradient,
    accentColor: cat.accentColor,
    description: t(`gamesScreen.categories.${cat.id}.description`),
    items: cat.items.map(item => ({
      id: item.id,
      title: t(`gamesScreen.categories.${cat.id}.items.${item.id}.title`),
      tagline: t(`gamesScreen.categories.${cat.id}.items.${item.id}.tagline`),
      accentIcon: item.accentIcon,
      color: item.color,
      gradientColors: item.gradientColors,
      component: item.component,
      detail: t(`gamesScreen.categories.${cat.id}.items.${item.id}.detail`),
    })),
  }));
}

// =====================================================
// POPUP DÉTAIL DU JEU
// =====================================================
const GameDetailPopup = ({
  item,
  visible,
  onClose,
  t,
}: {
  item: any;
  visible: boolean;
  onClose: () => void;
  t: (key: string) => string;
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(60)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 220,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          speed: 18,
          bounciness: 6,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 180,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 60,
          duration: 180,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  if (!item) return null;

  // Sur web : overlay centré / sur mobile : slide depuis le bas
  const isWeb = Platform.OS === 'web';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      {/* Overlay sombre */}
      <Animated.View style={[styles.popupOverlay, { opacity: fadeAnim }]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />

        {/* Carte */}
        <Animated.View
          style={[
            styles.popupCard,
            isWeb && styles.popupCardWeb,
            {
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Gradient de fond */}
          <LinearGradient
            colors={item.gradientColors}
            style={StyleSheet.absoluteFill}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
          />

          {/* Orbe décoratif */}
          <View
            style={[
              styles.popupOrb,
              { backgroundColor: item.color, opacity: 0.18 },
            ]}
          />

          {/* Bouton fermer */}
          <TouchableOpacity style={styles.popupCloseBtn} onPress={onClose}>
            <Ionicons name="close" size={18} color="rgba(255,255,255,0.8)" />
          </TouchableOpacity>

          {/* Icône centrale */}
          <View style={[styles.popupIconWrap, { borderColor: item.color + '60' }]}>
            <Ionicons name={item.accentIcon} size={34} color="#FFFFFF" />
          </View>

          {/* Titre + tagline */}
          <Text style={styles.popupTitle}>{item.title}</Text>
          <Text style={[styles.popupTagline, { color: item.color === '#64748B' ? '#94A3B8' : item.color }]}>
            {item.tagline}
          </Text>

          {/* Séparateur */}
          <View style={[styles.popupLine, { backgroundColor: item.color + '40' }]} />

          {/* Description */}
          <ScrollView
            style={styles.popupScrollArea}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            <Text style={styles.popupDetail}>{item.detail}</Text>
          </ScrollView>

          {/* Badge trophée */}
          <View style={styles.popupTrophyBadge}>
            <Ionicons name="trophy" size={14} color={BRAND.gold} />
            <Text style={styles.popupTrophyText}>
              {t('gamesScreen.trophiesAwardedPublicly')}
            </Text>
          </View>

          {/* CTA */}
          <TouchableOpacity
            style={[styles.popupCTA, { backgroundColor: item.color }]}
            onPress={onClose}
            activeOpacity={0.85}
          >
            <Ionicons name="flash" size={15} color="#FFFFFF" />
            <Text style={styles.popupCTAText}>{t('gamesScreen.startAdventure')}</Text>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

// =====================================================
// ITEM CARD
// =====================================================
const ItemCard = ({
  item,
  onPress,
  onLongPress,
}: {
  item: any;
  onPress: () => void;
  onLongPress: () => void;
}) => {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () =>
    Animated.spring(scale, {
      toValue: 0.91,
      useNativeDriver: true,
      speed: 60,
    }).start();

  const handlePressOut = () =>
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
      speed: 35,
    }).start();

  const handleLongPress = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    }
    // Petit "pulse" visuel au long press
    Animated.sequence([
      Animated.spring(scale, { toValue: 0.85, useNativeDriver: true, speed: 80 }),
      Animated.spring(scale, { toValue: 1.05, useNativeDriver: true, speed: 40 }),
      Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 30 }),
    ]).start();
    onLongPress();
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onLongPress={handleLongPress}
      delayLongPress={420}
      activeOpacity={1}
    >
      <Animated.View
        style={[
          styles.itemCard,
          {
            transform: [{ scale }],
            shadowColor: item.color,
          },
        ]}
      >
        <LinearGradient
          colors={item.gradientColors}
          style={styles.itemCardInner}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <View style={styles.itemIconCircle}>
            <Ionicons name={item.accentIcon} size={24} color="#FFFFFF" />
          </View>
          <Text style={styles.itemTitle}>{item.title}</Text>

          {/* Indicateur "appui long" discret */}
          <View style={styles.itemInfoDot}>
            <Ionicons name="information-circle" size={10} color="rgba(255,255,255,0.4)" />
          </View>
        </LinearGradient>
      </Animated.View>
    </TouchableOpacity>
  );
};

// =====================================================
// CATEGORY SECTION
// =====================================================
const CategorySection = ({
  category,
  onItemPress,
  onItemLongPress,
  t,
}: {
  category: any;
  onItemPress: (item: any) => void;
  onItemLongPress: (item: any) => void;
  t: (key: string) => string;
}) => (
  <View style={styles.categoryWrapper}>
    <LinearGradient
      colors={category.gradient}
      style={styles.categoryCard}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      {/* Header */}
      <View style={styles.catHeader}>
        <View style={[styles.catIconWrap, { borderColor: category.accentColor + '50' }]}>
          <Ionicons name={category.ionIcon} size={22} color={category.accentColor} />
        </View>
        <View style={styles.catInfo}>
          <Text style={styles.catTitle}>{category.title}</Text>
          <Text style={[styles.catDesc, { color: category.accentColor }]}>
            {category.description}
          </Text>
        </View>
      </View>

      {/* Séparateur */}
      <View style={[styles.catLine, { backgroundColor: category.accentColor + '25' }]} />

      {/* Items */}
      <View style={styles.itemsRow}>
        {category.items.map((item: any) => (
          <ItemCard
            key={item.id}
            item={item}
            onPress={() => onItemPress(item)}
            onLongPress={() => onItemLongPress(item)}
          />
        ))}
      </View>

      {/* Hint appui long */}
      <View style={styles.longPressHint}>
        <Ionicons name="hand-left-outline" size={10} color={category.accentColor + '70'} />
        <Text style={[styles.longPressHintText, { color: category.accentColor + '70' }]}>
          {t('gamesScreen.longPressHint')}
        </Text>
      </View>
    </LinearGradient>
  </View>
);

// =====================================================
// ÉCRAN PRINCIPAL
// =====================================================
export default function GamesScreen() {
  const { t } = useTranslation();
  const CATEGORIES = getCategories(t);

  const INFO_BANNER_CONTENT = {
    title: t('gamesScreen.infoBanner.title'),
    text: t('gamesScreen.infoBanner.text'),
  };

  const [selectedGame, setSelectedGame]           = useState<any>(null);
  const [modalVisible, setModalVisible]           = useState(false);
  const [detailItem, setDetailItem]               = useState<any>(null);
  const [detailVisible, setDetailVisible]         = useState(false);
  const [infoBannerVisible, setInfoBannerVisible] = useState(false);

  // ── Auto-hide tab bar au scroll (pas de header local ici, seule la
  // tab bar globale du (tabs)/_layout.tsx est pilotée) ─────────────────
  const { handleScroll, handleDoubleTap } = useScrollAutoHide();

  // ── Ouvrir le jeu ──────────────────────────────────
  const handleItemPress = (item: any) => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }
    setSelectedGame(item);
    setModalVisible(true);
  };

  const handleCloseGame = () => {
    setModalVisible(false);
    setTimeout(() => setSelectedGame(null), 300);
  };

  // ── Ouvrir le popup détail ──────────────────────────
  const handleItemLongPress = (item: any) => {
    setDetailItem(item);
    setDetailVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailVisible(false);
    setTimeout(() => setDetailItem(null), 300);
  };

  // ── Toggle bandeau info ─────────────────────────────
  const toggleInfoBanner = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setInfoBannerVisible((v) => !v);
  };

  return (
    <View style={styles.container}>

      {/* ── HEADER ── */}
      <LinearGradient
        colors={[BRAND.purpleDark, BRAND.purple, '#A020F0']}
        style={styles.header}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        {/* Orbes décoratifs */}
        <View style={[styles.orb, { width: 180, height: 180, top: -55, right: -45, backgroundColor: BRAND.orange, opacity: 0.14 }]} />
        <View style={[styles.orb, { width: 90, height: 90, bottom: -20, left: 15, backgroundColor: BRAND.gold, opacity: 0.10 }]} />

        <View style={styles.headerContent}>
          {/* Ligne Logo + bouton Info */}
          <View style={styles.headerTopRow}>
            <HarmoniaLogo size={50} showText={true} theme="light" />

            {/* Bouton ⓘ Info */}
            <TouchableOpacity
              onPress={toggleInfoBanner}
              style={[
                styles.infoBtnWrap,
                infoBannerVisible && styles.infoBtnActive,
              ]}
              activeOpacity={0.8}
            >
              <Ionicons
                name={infoBannerVisible ? 'close-circle' : 'information-circle-outline'}
                size={22}
                color={infoBannerVisible ? BRAND.gold : 'rgba(255,255,255,0.75)'}
              />
            </TouchableOpacity>
          </View>

          {/* Pill "Centre des Talents" */}
          <View style={styles.taglinePill}>
            <Ionicons name="star" size={11} color={BRAND.gold} />
            <Text style={styles.taglineText}>{t('gamesScreen.header.pill')}</Text>
            <Ionicons name="star" size={11} color={BRAND.gold} />
          </View>

          {/* Titre */}
          <Text style={styles.heroTitle}>{t('gamesScreen.header.title')}</Text>
        </View>

        {/*
          ══════════════════════════════════════════════════════════════
          FIX BUG "header qui gonfle / clics qui ratent" :
          InfoBanner est maintenant en position ABSOLUE, ancré juste sous
          le header (top: HEADER_APPROX_HEIGHT), au lieu d'être un enfant
          normal du header. Avant, la carte dépliée de l'InfoBanner
          (hauteur variable selon le texte) poussait littéralement tout
          le contenu du header vers le bas à chaque tap — d'où l'effet
          "ça gonfle comme un bug" et les taps suivants qui touchaient
          la mauvaise zone parce que les éléments avaient bougé sous le
          doigt pendant l'animation.
          En sortant l'InfoBanner du flux normal (position: 'absolute',
          zIndex élevé), son expansion se fait PAR-DESSUS le contenu
          existant, sans jamais redimensionner le header ni pousser quoi
          que ce soit d'autre à l'écran. Les positions de tous les autres
          éléments restent fixes, donc plus aucun clic ne peut "rater".

          MISE À JOUR : la rangée de badges (Trophées/Live/Communauté) a
          été supprimée pour libérer de la place — l'InfoBanner remonte
          donc directement sous le titre "Révélez votre Talent", au lieu
          d'être caché plus bas derrière/sous cette rangée. Voir
          HEADER_CONTENT_APPROX_HEIGHT, réduite en conséquence.

          MISE À JOUR v3 (InfoBanner) : la carte dépliée de l'InfoBanner
          propose maintenant un bouton "agrandir" qui ouvre une vraie
          Modal plein écran (composant Modal de React Native — rendu
          natif sur mobile, overlay DOM sur web, donc aucun code
          spécifique par plateforme à gérer ici). Cette Modal se rend
          dans une couche séparée du système, au-dessus de tout —
          aucun conflit possible avec le zIndex/elevation de
          infoBannerOverlay ci-dessous ni avec la tab bar globale du
          (tabs)/_layout.tsx. Props de InfoBanner INCHANGÉES (visible,
          title, text) : aucune adaptation nécessaire ici pour que la
          Modal plein écran fonctionne — tout est géré en interne par
          le composant.
          ══════════════════════════════════════════════════════════════
        */}
        <View style={styles.infoBannerOverlay} pointerEvents="box-none">
          <InfoBanner
            visible={infoBannerVisible}
            title={INFO_BANNER_CONTENT.title}
            text={INFO_BANNER_CONTENT.text}
          />
        </View>
      </LinearGradient>

      {/* ── SCROLL ── */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        {/* Label section */}
        <View style={styles.sectionLabelRow}>
          <View style={styles.sectionDot} />
          <Text style={styles.sectionLabel}>{t('gamesScreen.chooseDiscipline')}</Text>
          <View style={styles.sectionDot} />
        </View>

        {CATEGORIES.map((cat) => (
          <CategorySection
            key={cat.id}
            category={cat}
            onItemPress={handleItemPress}
            onItemLongPress={handleItemLongPress}
            t={t}
          />
        ))}

        {/* Note bas de page */}
        <View style={styles.footerNote}>
          <Ionicons name="information-circle-outline" size={15} color={BRAND.textMuted} />
          <Text style={styles.footerNoteText}>
            {t('gamesScreen.footerNote')}
          </Text>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>

      {/* ── MODAL JEU ── */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={handleCloseGame}
      >
        <View style={styles.modalContainer}>
          {selectedGame?.component && (
            <selectedGame.component
              title={selectedGame.title}
              icon={selectedGame.accentIcon}
              color={selectedGame.color}
              onClose={handleCloseGame}
            />
          )}
        </View>
      </Modal>

      {/* ── POPUP DÉTAIL JEU (long press) ── */}
      <GameDetailPopup
        item={detailItem}
        visible={detailVisible}
        onClose={handleCloseDetail}
        t={t}
      />
    </View>
  );
}

// =====================================================
// STYLES
// =====================================================
const ITEM_SIZE = (width - 72) / 4;

// Hauteur approximative de la zone "contenu" du header (logo + pill + titre),
// utilisée pour ancrer l'overlay de l'InfoBanner juste en dessous.
// Réduite après suppression de la rangée de badges (Trophées/Live/
// Communauté) — l'InfoBanner remonte donc directement sous le titre.
// Cette valeur n'a pas besoin d'être pixel-perfect : l'InfoBanner a son
// propre marginTop interne (outerContainer) qui absorbe les petits écarts.
const HEADER_CONTENT_APPROX_HEIGHT = Platform.OS === 'ios' ? 160 : 140;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BRAND.bg,
  },

  // ── Header ─────────────────────────────────────────
  header: {
    paddingTop: Platform.OS === 'ios' ? 54 : 32,
    paddingBottom: 26,
    overflow: 'hidden',
  },
  orb: {
    position: 'absolute',
    borderRadius: 9999,
  },
  headerContent: {
    paddingHorizontal: 22,
    alignItems: 'center',
    gap: 12,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  infoBtnWrap: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.22)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoBtnActive: {
    backgroundColor: 'rgba(255,215,0,0.15)',
    borderColor: 'rgba(255,215,0,0.45)',
  },
  taglinePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(0,0,0,0.28)',
    borderWidth: 1,
    borderColor: 'rgba(255,215,0,0.32)',
    paddingHorizontal: 14,
    paddingVertical: 5,
    borderRadius: 100,
  },
  taglineText: {
    color: BRAND.gold,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: '900',
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: -0.3,
  },

  // ── InfoBanner overlay ─────────────────────────────
  // Positionné en absolu par-dessus le reste : son expansion ne pousse
  // jamais le header ni le scroll en dessous. zIndex/elevation élevés
  // pour qu'il flotte visuellement au-dessus de tout le reste de l'écran
  // (y compris le contenu du ScrollView) quand la carte est dépliée.
  infoBannerOverlay: {
    position: 'absolute',
    top: HEADER_CONTENT_APPROX_HEIGHT,
    left: 0,
    right: 0,
    zIndex: 50,
    elevation: 50,
  },

  // ── Scroll ─────────────────────────────────────────
  scroll: { flex: 1 },
  scrollContent: { paddingTop: 4, paddingBottom: 20 },

  sectionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 20,
    marginBottom: 12,
  },
  sectionDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: BRAND.orange,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: BRAND.textMuted,
    letterSpacing: 2.5,
  },

  // ── Category ───────────────────────────────────────
  categoryWrapper: {
    paddingHorizontal: 14,
    marginBottom: 11,
  },
  categoryCard: {
    borderRadius: 22,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.05)',
    overflow: 'hidden',
  },
  catHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    marginBottom: 12,
  },
  catIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 13,
    borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  catInfo: { flex: 1 },
  catTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: -0.2,
    marginBottom: 1,
  },
  catDesc: {
    fontSize: 11,
    fontWeight: '500',
    letterSpacing: 0.2,
  },
  catLine: {
    height: 1,
    borderRadius: 1,
    marginBottom: 12,
  },
  longPressHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 10,
  },
  longPressHintText: {
    fontSize: 10,
    fontStyle: 'italic',
    letterSpacing: 0.1,
  },

  // ── Items ──────────────────────────────────────────
  itemsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 9,
  },
  itemCard: {
    width: ITEM_SIZE,
    borderRadius: 16,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 8,
    elevation: 6,
  },
  itemCardInner: {
    paddingVertical: 14,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 82,
    gap: 7,
  },
  itemIconCircle: {
    width: 42,
    height: 42,
    borderRadius: 13,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.13)',
  },
  itemTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.95)',
    textAlign: 'center',
    letterSpacing: 0.1,
  },
  itemInfoDot: {
    position: 'absolute',
    top: 5,
    right: 6,
  },

  // ── Footer note ────────────────────────────────────
  footerNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    justifyContent: 'center',
    marginTop: 20,
    marginHorizontal: 14,
    paddingVertical: 11,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(138,43,226,0.1)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(138,43,226,0.22)',
  },
  footerNoteText: {
    fontSize: 12,
    color: BRAND.textMuted,
    fontStyle: 'italic',
  },

  // ── Modal jeu ──────────────────────────────────────
  modalContainer: {
    flex: 1,
    backgroundColor: BRAND.bg,
  },

  // ── Popup détail jeu ───────────────────────────────
  popupOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.72)',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  popupCard: {
    width: '100%',
    maxHeight: height * 0.72,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    overflow: 'hidden',
    paddingHorizontal: 22,
    paddingTop: 28,
    paddingBottom: 32,
    alignItems: 'center',
    gap: 12,
  },
  // Sur web : carte centrée et arrondie partout
  popupCardWeb: {
    width: Math.min(width - 48, 420),
    maxHeight: height * 0.75,
    borderRadius: 28,
    marginBottom: height * 0.1,
  },
  popupOrb: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    top: -60,
    right: -60,
  },
  popupCloseBtn: {
    position: 'absolute',
    top: 14,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popupIconWrap: {
    width: 72,
    height: 72,
    borderRadius: 22,
    borderWidth: 1.5,
    backgroundColor: 'rgba(255,255,255,0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  popupTitle: {
    fontSize: 22,
    fontWeight: '900',
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: -0.3,
  },
  popupTagline: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
    letterSpacing: 0.3,
    fontStyle: 'italic',
  },
  popupLine: {
    width: '60%',
    height: 1,
    borderRadius: 1,
    marginVertical: 2,
  },
  popupScrollArea: {
    maxHeight: 160,
    width: '100%',
  },
  popupDetail: {
    fontSize: 13.5,
    lineHeight: 21,
    color: 'rgba(255,255,255,0.82)',
    textAlign: 'center',
    letterSpacing: 0.1,
    paddingHorizontal: 4,
  },
  popupTrophyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderWidth: 1,
    borderColor: 'rgba(255,215,0,0.35)',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 100,
    marginTop: 2,
  },
  popupTrophyText: {
    fontSize: 11,
    fontWeight: '700',
    color: BRAND.gold,
    letterSpacing: 0.2,
  },
  popupCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 28,
    paddingVertical: 13,
    borderRadius: 14,
    marginTop: 4,
    width: '100%',
    justifyContent: 'center',
  },
  popupCTAText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: 0.2,
  },
});
